import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../baseComponents/Button'
import { TwinIconToolTip } from '../../../../../../../../../../baseComponents/TwinIcon'
import { OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import twinFetchPost from '../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../utils/globals/link'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { useLocation, useNavigate } from 'react-router'
import PermissionChecker from '../../../../../../../../../../baseComponents/PermissionChecker'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import useEditDeleteModal from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import { checkBuyInvoiceAllOk } from '../../../../../../../../../../utils/globals/buy/buy'
import { BuyDownloadButtonCSV, BuyDownloadButtonPDF, BuyDownloadPdfTemplateButton, BuyDownloadTags, BuyPdfPreview, BuySupplierPdfPreview } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/BuyButtons'
import ModalDeleteBuyInvoiceDraft from './ModalDeleteBuyInvoiceDraft'
import { BuyInvoiceDuplicateButton } from '../../../BuyInvoiceButtons'
import { changeErrorMessage } from '../../../../../../../../../../utils/reducers/reduxDispatch'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { BuyInvoiceStatusInversed } from '@teinor/erp/types/company/order/buy/buyInvoice'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'

const BuyInvoiceCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageBuy } = useBuyInvoiceCreateButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useBuyInvoiceCreateButtonsLogic, ...rest }} className='mr-8' />
            <BuyInvoiceCreateButton  title={t('processBuyInvoice', 'Procesar factura')} disabled={infoMessageBuy ? true : false} OrderId={OrderId} onSubmit={onSubmit} infoMessage={infoMessageBuy || ''} />
        </div>
    )
}

const useBuyInvoiceCreateButtonsLogic = ({ data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'buy_invoice_create_step_buttons'
    let infoMessageBuy = checkBuyInvoiceAllOk(data, t)
    const Order = data?.header
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyBuy = orderEmployeeId === EmployeeId
    const hideDeleteButton = !isMyBuy || Order?.BuyOrderId || Order?.BuyDeliveryNoteId

    const defaultButtons = [{ "id": "previewBuyInvoicePDF" }, { "id": "downloadBuyInvoice" }, { "id": "duplicateBuyInvoice" }, { "id": "deleteBuyInvoice" }]

    const buyInvoiceDuplicateText = t('duplicateBuyInvoice', 'Duplicar factura de compra')
    const buyInvoiceDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const buyInvoiceDeleteText = t('deleteInvoiceDraft', 'Eliminar borrador factura')
    const buyInvoiceDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const buyInvoicePreviewPDFText = t('previewPdfTemplate', 'Previsualizar plantilla PDF')
    const buyInvoiceDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const buyInvoiceDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const buyInvoiceDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const buyInvoiceVariablesText = t('variables', 'Variables')
    const buyOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')
    const buyOrderPreviewSupplierPDFText = t('previewSupplierPDF', 'Previsualizar PDF proveedor')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateBuyInvoice: {
                component: BuyInvoiceDuplicateButton,
                extraProps: {
                    title: buyInvoiceDuplicateText,
                    id: 'duplicateBuyInvoice',
                    allRowData: data,
                    permissionTy: 'buy.invoice',
                }
            },
            deleteBuyInvoice: {
                component: BuyInvoiceDeleteButton,
                showButton: !hideDeleteButton,
                extraProps: {
                    title: buyInvoiceDeleteText,
                    id: 'deleteBuyInvoice',
                    allRowData: data,
                }
            },
            downloadBuyCsv: {
                component: BuyDownloadButtonCSV,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyInvoiceDownloadCSVText,
                    id: 'downloadBuyCsv',
                    Order,
                    typeTemplate: 'buyInvoice'
                }
            },
            downloadBuyTag: {
                component: BuyDownloadTags,
                extraProps: {
                    title: buyInvoiceDownloadTagsText,
                    id: 'downloadBuyTag',
                    Order,
                    typeTemplate: 'buyInvoice'
                }
            },
            previewBuySupplierPDF: {
                component: BuySupplierPdfPreview,
                showButton: Order.supplierPdf?.length,
                extraProps: {
                    title: buyOrderPreviewSupplierPDFText,
                    id: 'previewBuyOrderPDFSupplier',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'previewBuyOrderPDFSupplier'
                }
            },
            previewBuyInvoicePDF: {
                component: BuyPdfPreview,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyInvoicePreviewPDFText,
                    id: 'previewBuyInvoicePDF',
                    Order,
                    typeTemplate: 'buyInvoice',
                    key: 'previewBuyInvoicePDF'
                }
            },
            downloadBuyTemplate: {
                component: BuyDownloadPdfTemplateButton,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyInvoiceDownloadPDFTemplateText,
                    id: 'downloadBuyTemplate',
                    Order,
                    typeTemplate: 'buyInvoice',
                    key: 'downloadBuyInvoicePDFTemplate'
                }
            },
            downloadBuyInvoice: {
                component: BuyDownloadButtonPDF,
                showButton: !infoMessageBuy,
                extraProps: {
                    title: buyInvoiceDownloadPDFText,
                    id: 'downloadBuyInvoice',
                    Order,
                    typeTemplate: 'buyInvoice',
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: buyInvoiceDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: buyInvoiceVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            buyProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: buyOrderProductTypesText,
                    id: 'buyProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData,
                }
            },
        }
    }, [Order, buyInvoiceDivideIntoChaptersText, buyInvoiceDownloadCSVText, buyInvoiceDownloadPDFTemplateText, buyInvoiceDownloadPDFText, buyInvoiceDownloadTagsText, buyInvoiceDuplicateText, buyInvoicePreviewPDFText, data, onSubmit, buyInvoiceVariablesText, buyInvoiceDeleteText, hideDeleteButton, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions, infoMessageBuy, buyOrderProductTypesText, buyOrderPreviewSupplierPDFText])

    return { name, ButtonComponents, infoMessageBuy, defaultButtons }
}

export const BuyInvoiceCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageBuy } = useBuyInvoiceCreateButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useBuyInvoiceCreateButtonsLogic, ...rest }} className='mr-8' />
            <BuyInvoiceCreateButton title={t('processBuyInvoice', 'Procesar factura')} disabled={infoMessageBuy ? true : false} OrderId={OrderId} onSubmit={onSubmit} infoMessage={infoMessageBuy || ''} />
        </div>
    )
}

type BuyInvoiceCreateButtonProps = BuyInvoiceCreateButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const BuyInvoiceCreateButton: React.FC<BuyInvoiceCreateButtonProps> = ({ title, OrderId, onSubmit, disabled, infoMessage, ...rest }) => {
    const { finishInvoice, loading } = useBuyInvoiceCreateButtonLogic({ OrderId, onSubmit })
    return (
        <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
            <div className='min-w-175'>
                <ButtonDisabledOrLoader onClick={finishInvoice} buttonIsDisabled={disabled} textButton={title} loading={loading} className='w-full' />
            </div>
        </ButtonOrderInfoLeftBase>
    )
}

interface BuyInvoiceCreateButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
}

const useBuyInvoiceCreateButtonLogic = ({ onSubmit, OrderId }: BuyInvoiceCreateButtonLogicProps) => {
    const {t} = useTwinTranslation()
    const errorUniqueNumberNLine = t('errorWithTheUniqueNumberNLine', 'Existe un error con el número de serie de la línea')
    const errorQuantityUniqueNumberNLine = t('errorQuantityWithTheUniqueNumberNLine', 'Existe un error con la cantidad del número de serie de la línea')
    const duplicateUniqueNumber = t('alreadyExistsUniqueNumber', 'Ya existe un producto con el número de serie')
    const noWarehouses = t('noWarehousesInWorkingCenter', 'Este centro de trabajo no tiene ningún almacén asociado')
    const { startLoading, endLoading, loading } = useIsLoading()
    
    const finishInvoice = useCallback(async () => {
        startLoading?.()
        const response = await twinFetchPost('/api/app/order/buy/buyInvoice/updateBuyInvoice', { id: OrderId, status: BuyInvoiceStatusInversed['accepted'] })
        if (response.status === 200) {
            onSubmit()
        } else {
            const responseJSON = await response.json()
            const error = responseJSON.errors[0].msg
            const splitError = error.split('-')
            if (splitError[0] === 'This serial number already exists') {
                changeErrorMessage(duplicateUniqueNumber + ' ' + splitError[1])
            } else if (splitError[0] === 'Serial Number is required') {
                changeErrorMessage(errorUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'This serial number has quantity problem') {
                changeErrorMessage(errorQuantityUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'noWarehouses') {
                changeErrorMessage(noWarehouses)
            }
        }
        onSubmit()
        endLoading?.()
    }, [OrderId, onSubmit, errorUniqueNumberNLine, errorQuantityUniqueNumberNLine, duplicateUniqueNumber, noWarehouses, startLoading, endLoading])

    return { finishInvoice, loading }
}

interface BuyInvoiceDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const BuyInvoiceDeleteButton: React.FC<BuyInvoiceDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useBuyInvoiceDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('buy.invoice')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteBuyInvoiceDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
)
}

const useBuyInvoiceDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'invoices')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

export default BuyInvoiceCreateStepButtons